import { useContext, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskedLine, Reveal, ClipReveal } from "@/components/Motion";
import { LenisContext } from "@/App";

const RITUALS = [
  { numeral: "I", title: "The Morning Alchemy", text: "Begin the day by turning ordinary moments into intentional practice. Through herbs, breathwork, reflection, and mindful habits, the morning becomes a space to alchemize stagnant energy into clarity, grounding, and renewed purpose. It is a daily reminder that growth can begin with small, conscious choices." },
  { numeral: "II", title: "The Inner Ritual", text: "Create space to reconnect with yourself through journaling, tea, meditation, stretching, and intentional rest. These quiet practices support reflection, emotional renewal, and self-awareness, allowing difficult experiences to become opportunities for deeper understanding, balance, and personal growth." },
  { numeral: "III", title: "The Healing Practice", text: "Approach healing as a holistic relationship between mind, body, and nature. Herbal infusions, steams, nourishment, rest, and gentle rituals encourage you to slow down, listen inward, and support your well-being naturally. In this practice, healing is not about erasing the past, but allowing something meaningful and beautiful to grow from it. Healing becomes a process of renewal, creating space for greater balance, self-connection, and growth." },
];

export default function About() {
  const { hash } = useLocation();
  const lenisRef = useContext(LenisContext);

  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => lenisRef.current?.scrollTo(hash, { offset: -60 }), 150);
      return () => clearTimeout(t);
    }
  }, [hash, lenisRef]);

  return (
    <main data-testid="about-page" className="pt-24">
      <header className="mx-auto max-w-[1600px] px-6 pb-24 pt-16 lg:px-12 lg:pt-24">
        <Reveal y={16}>
          <p className="mb-8 text-xs uppercase tracking-[0.4em] text-gilt">Rituals & Philosophy</p>
        </Reveal>
        <h1 className="font-serif text-6xl leading-[0.98] text-ivory lg:text-8xl" data-testid="about-headline">
          <MaskedLine delay={0.1}>Ritual rooted in</MaskedLine>
          <MaskedLine delay={0.25} className="italic text-gilt">intention and becoming.</MaskedLine>
        </h1>
      </header>

      <section className="mx-auto grid max-w-[1600px] gap-16 px-6 pb-28 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-7">
          <Reveal>
            <p data-testid="about-story-1" className="font-serif text-2xl leading-relaxed text-ivory lg:text-3xl">
              Turn ordinary moments into intentional practices that support
              holistic growth and healing. Through herbs, reflection, breathwork,
              and mindful routines, Faeloria embraces the belief that beauty and
              growth can emerge from experiences that once felt overwhelming.
              Each ritual becomes an opportunity to alchemize stagnant energy
              into clarity, renewal, and deeper personal growth.
            </p>
          </Reveal>

          <div id="rituals" className="mt-24 scroll-mt-24">
            <Reveal>
              <p className="mb-10 text-xs uppercase tracking-[0.4em] text-gilt">The Three Rituals</p>
            </Reveal>
            {RITUALS.map((r, i) => (
              <Reveal key={r.numeral} delay={i * 0.08}>
                <div data-testid={`ritual-chapter-${i + 1}`} className="border-t border-moss-700 py-12">
                  <p className="font-serif text-5xl italic text-gilt/50">{r.numeral}</p>
                  <h3 className="mt-5 font-serif text-3xl text-ivory">{r.title}</h3>
                  <p className="mt-5 max-w-lg text-sm leading-relaxed text-sand">{r.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <ClipReveal delay={0.25} className="border border-moss-700 bg-moss-900 p-3">
              <div className="overflow-hidden">
                <img
                  data-testid="about-logo-artwork"
                  src="/logo.webp"
                  alt="Faeloria hand-painted logo artwork"
                  className="aspect-[4/5] w-full object-cover"
                />
              </div>
            </ClipReveal>
          </div>
        </div>
      </section>

      <section className="border-t border-moss-700/60 bg-moss-900">
        <div className="mx-auto flex max-w-[1600px] flex-col items-start gap-10 px-6 py-24 lg:flex-row lg:items-center lg:justify-between lg:px-12">
          <h2 className="max-w-xl font-serif text-4xl leading-tight text-ivory lg:text-5xl" data-testid="about-cta-heading">
            Six formulations, <span className="italic text-gilt">each a quiet ritual.</span>
          </h2>
          <Link
            data-testid="about-cta-button"
            to="/"
            className="group inline-flex items-center gap-4 border border-gilt px-8 py-4 text-xs uppercase tracking-[0.3em] text-ivory transition-colors duration-500 hover:bg-gilt hover:text-[#EFE8D2]"
          >
            Browse the Collection
            <ArrowUpRight size={14} className="transition-transform duration-500 group-hover:-translate-y-1 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </main>
  );
}
